import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiExternalLink, FiBookOpen } = FiIcons;

const ReviewCard = ({ review }) => {
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - Math.ceil(rating);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Full stars */}
        {[...Array(fullStars)].map((_, index) => (
          <SafeIcon
            key={`full-${index}`}
            icon={FiStar}
            style={{ width: '16px', height: '16px', color: '#00ff88' }}
          />
        ))}
        
        {/* Half star */}
        {hasHalfStar && (
          <div style={{ position: 'relative' }}>
            <SafeIcon
              icon={FiStar}
              style={{ width: '16px', height: '16px', color: '#333' }}
            />
            <div style={{ 
              position: 'absolute', 
              inset: '0', 
              overflow: 'hidden', 
              width: '50%' 
            }}>
              <SafeIcon
                icon={FiStar}
                style={{ width: '16px', height: '16px', color: '#00ff88' }}
              />
            </div>
          </div>
        )}
        
        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, index) => (
          <SafeIcon
            key={`empty-${index}`}
            icon={FiStar}
            style={{ width: '16px', height: '16px', color: '#333' }}
          />
        ))}
        
        <span style={{ 
          marginLeft: '8px', 
          fontSize: '14px', 
          color: '#00ff88', 
          fontWeight: '600' 
        }}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  // Truncate text helper function
  const truncateText = (text, maxLength = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <motion.div 
      className="tool-card"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      style={{
        // Ensure the card has proper boundaries
        overflow: 'hidden',
        wordWrap: 'break-word',
        hyphens: 'auto'
      }}
    >
      {/* Header */}
      <div className="tool-header">
        <div className="tool-icon">📝</div>
        <div className="tool-info" style={{ 
          minWidth: 0, // Allow shrinking
          flex: 1 // Take available space
        }}>
          <div 
            className="tool-title" 
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}
          >
            {review.title}
          </div>
          <div style={{ marginTop: '8px' }}>
            {renderStars(review.rating)}
          </div>
        </div>
      </div>

      {/* Description */}
<div 
  className="tool-description" 
  style={{ 
    marginBottom: '20px',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.5',
    maxHeight: '4.5em' // 3 lines * 1.5 line-height
  }}
>
  {review.description.split('\n\n').map((paragraph, index) => (
    <p key={index} style={{ marginBottom: '1em' }}>
      {paragraph}
    </p>
  ))}
</div>

      {/* Pros/Cons Preview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '16px', 
        marginBottom: '20px',
        minHeight: '120px' // Consistent height for alignment
      }}>
        <div style={{ 
          minWidth: 0, // Allow shrinking
          overflow: 'hidden'
        }}>
          <h4 style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: '#00ff88', 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            marginBottom: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            ✓ Pros
          </h4>
          <ul style={{ 
            listStyle: 'none', 
            padding: '0', 
            margin: '0',
            height: '80px',
            overflow: 'hidden'
          }}>
            {review.pros.slice(0, 2).map((pro, index) => (
              <li key={index} style={{ 
                fontSize: '12px', 
                color: '#b0b0b0', 
                display: 'flex', 
                alignItems: 'flex-start',
                marginBottom: '6px',
                lineHeight: '1.4',
                minWidth: 0 // Allow shrinking
              }}>
                <span style={{ 
                  color: '#00ff88', 
                  marginRight: '6px',
                  flexShrink: 0 // Don't shrink the bullet
                }}>•</span>
                <span style={{ 
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordWrap: 'break-word',
                  hyphens: 'auto',
                  minWidth: 0 // Allow shrinking
                }}>
                  {truncateText(pro, 50)}
                </span>
              </li>
            ))}
            {review.pros.length > 2 && (
              <li style={{ 
                fontSize: '12px', 
                color: '#666',
                marginTop: '4px'
              }}>
                +{review.pros.length - 2} more
              </li>
            )}
          </ul>
        </div>

        <div style={{ 
          minWidth: 0, // Allow shrinking
          overflow: 'hidden'
        }}>
          <h4 style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: '#ff00ff', 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            marginBottom: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            ✗ Cons
          </h4>
          <ul style={{ 
            listStyle: 'none', 
            padding: '0', 
            margin: '0',
            height: '80px',
            overflow: 'hidden'
          }}>
            {review.cons.slice(0, 2).map((con, index) => (
              <li key={index} style={{ 
                fontSize: '12px', 
                color: '#b0b0b0', 
                display: 'flex', 
                alignItems: 'flex-start',
                marginBottom: '6px',
                lineHeight: '1.4',
                minWidth: 0 // Allow shrinking
              }}>
                <span style={{ 
                  color: '#ff00ff', 
                  marginRight: '6px',
                  flexShrink: 0 // Don't shrink the bullet
                }}>•</span>
                <span style={{ 
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordWrap: 'break-word',
                  hyphens: 'auto',
                  minWidth: 0 // Allow shrinking
                }}>
                  {truncateText(con, 50)}
                </span>
              </li>
            ))}
            {review.cons.length > 2 && (
              <li style={{ 
                fontSize: '12px', 
                color: '#666',
                marginTop: '4px'
              }}>
                +{review.cons.length - 2} more
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="tool-actions" style={{
        display: 'flex',
        gap: '8px',
        minWidth: 0 // Allow shrinking
      }}>
        <Link
          to={`/reviews/${review.slug}`}
          className="btn btn-primary"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '8px',
            flex: '1',
            minWidth: 0, // Allow shrinking
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: 'none'
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Read Full Review
          </span>
          <SafeIcon icon={FiBookOpen} style={{ 
            width: '16px', 
            height: '16px',
            flexShrink: 0 
          }} />
        </Link>
        
        {review.affiliateLink && (
          <motion.a
            href={review.affiliateLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '80px',
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Try Tool
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

export default ReviewCard;